import {
  authDescriptionSlate,
  authHeadingSlate,
  authTopBarBorder,
  palettePrimaryMain,
  authCardSurfaceSx,
} from '@/theme/designTokens'

/** Figma 5074-11964 / 5074-12873 — Main Verification Card */
export const SIGNUP_VERIFY_CARD_WIDTH_PX = 480
export const SIGNUP_VERIFY_CARD_PADDING_PX = 49
export const SIGNUP_VERIFY_CARD_SECTION_GAP_PX = 19
/** Interior content width — OTP row + Verify + Resend (Figma 382px) */
export const SIGNUP_VERIFY_CONTENT_WIDTH_PX = 382
export const SIGNUP_VERIFY_LOGO_BLOCK_HEIGHT_PX = 108
export const SIGNUP_VERIFY_HEADING_FRAME_HEIGHT_PX = 45
export const SIGNUP_VERIFY_HEADING_TO_SUBTITLE_GAP_PX = 8
export const SIGNUP_VERIFY_SUBTITLE_MIN_HEIGHT_PX = 48
export const SIGNUP_VERIFY_OTP_LABEL_MIN_HEIGHT_PX = 38
/** Figma 5074:12890–12895 — Input - Box 1…6 */
export const SIGNUP_VERIFY_OTP_BOX_WIDTH_PX = 60.09
export const SIGNUP_VERIFY_OTP_BOX_HEIGHT_PX = 64
export const SIGNUP_VERIFY_OTP_BOX_PADDING_X_PX = 14
export const SIGNUP_VERIFY_OTP_BOX_PADDING_Y_PX = 13
export const SIGNUP_VERIFY_OTP_DIGIT_FRAME_HEIGHT_PX = 38
export const SIGNUP_VERIFY_OTP_DIGIT_FRAME_WIDTH_PX = 29
export const SIGNUP_VERIFY_OTP_DIGIT_FONT_SIZE_PX = 28
export const SIGNUP_VERIFY_OTP_DIGIT_COLOR = '#0b1c30'
export const SIGNUP_VERIFY_OTP_BOX_BG = '#eff4ff'
export const SIGNUP_VERIFY_OTP_BOX_BORDER_WIDTH_PX = 2
/** (382 − 6×60.09) / 5 — Figma Group 1000002527 */
export const SIGNUP_VERIFY_OTP_CELL_GAP_PX =
  (SIGNUP_VERIFY_CONTENT_WIDTH_PX - 6 * SIGNUP_VERIFY_OTP_BOX_WIDTH_PX) / 5
export const SIGNUP_VERIFY_BUTTON_DISABLED_BG = '#94a3b8'
export const SIGNUP_VERIFY_RESEND_MIN_HEIGHT_PX = 27

export const signupVerifyCardSx = {
  ...authCardSurfaceSx,
  width: '100%',
  maxWidth: SIGNUP_VERIFY_CARD_WIDTH_PX,
  border: `1px solid ${authTopBarBorder}`,
  borderRadius: '12px',
  boxShadow: '0px 4px 2px rgba(0, 0, 0, 0.25)',
  p: { xs: 3, sm: `${SIGNUP_VERIFY_CARD_PADDING_PX}px` },
  display: 'flex',
  flexDirection: 'column',
  gap: `${SIGNUP_VERIFY_CARD_SECTION_GAP_PX}px`,
  alignItems: 'stretch',
  boxSizing: 'border-box',
  minWidth: 0,
} as const

export const signupVerifyLogoBlockSx = {
  height: { xs: 80, sm: SIGNUP_VERIFY_LOGO_BLOCK_HEIGHT_PX },
  minHeight: { xs: 80, sm: SIGNUP_VERIFY_LOGO_BLOCK_HEIGHT_PX },
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
} as const

export const signupVerifyLogoImgSx = {
  maxWidth: { xs: 200, sm: 240 },
  width: 'auto',
  height: 'auto',
  maxHeight: { xs: 80, sm: 96 },
  objectFit: 'contain',
  display: 'block',
} as const

export const signupVerifyTitleBlockSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: `${SIGNUP_VERIFY_HEADING_TO_SUBTITLE_GAP_PX}px`,
  alignItems: 'center',
  textAlign: 'center',
  width: '100%',
  flexShrink: 0,
} as const

export const signupVerifyHeadingFrameSx = {
  minHeight: SIGNUP_VERIFY_HEADING_FRAME_HEIGHT_PX,
  height: 'auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  overflow: 'visible',
} as const

export const signupVerifyHeadingSx = {
  fontWeight: 700,
  fontSize: { xs: '24px', sm: '32px' },
  lineHeight: { xs: '32px', sm: '40px' },
  letterSpacing: '0.4063px',
  color: authHeadingSlate,
  textAlign: 'center',
  m: 0,
  width: '100%',
} as const

export const signupVerifySubtitleSx = {
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '20px',
  color: authDescriptionSlate,
  textAlign: 'center',
  m: 0,
  minHeight: 'unset',
  width: '100%',
  px: { xs: 0.5, sm: 0 },
} as const

export const signupVerifyOtpLabelSx = {
  fontWeight: 600,
  fontSize: '18px',
  lineHeight: '28px',
  color: authHeadingSlate,
  textAlign: 'center',
  m: 0,
  minHeight: SIGNUP_VERIFY_OTP_LABEL_MIN_HEIGHT_PX,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  maxWidth: SIGNUP_VERIFY_CONTENT_WIDTH_PX,
  mx: 'auto',
  flexShrink: 0,
} as const

/** Figma — OTP row + primary CTA + resend share 382px inner width */
export const signupVerifyContentColumnSx = {
  width: '100%',
  maxWidth: SIGNUP_VERIFY_CONTENT_WIDTH_PX,
  mx: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: `${SIGNUP_VERIFY_CARD_SECTION_GAP_PX}px`,
  alignItems: 'stretch',
  minWidth: 0,
} as const

/** Six OTP boxes shrink together so they stay on one row on phones. */
export const signupVerifyOtpRowSx = {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'nowrap',
  gap: `clamp(4px, 1.5vw, ${SIGNUP_VERIFY_OTP_CELL_GAP_PX}px)`,
  alignItems: 'stretch',
  justifyContent: 'center',
  width: '100%',
  maxWidth: SIGNUP_VERIFY_CONTENT_WIDTH_PX,
  mx: 'auto',
  boxSizing: 'border-box',
  minWidth: 0,
} as const

export const signupVerifyPrimaryButtonSx = {
  borderRadius: '8px',
  py: '16px',
  px: '24px',
  minHeight: 56,
  fontSize: '16px',
  fontWeight: 500,
  lineHeight: '24px',
  letterSpacing: '0.15px',
  textTransform: 'none',
  boxShadow: 'none',
  width: '100%',
  '&.Mui-disabled': {
    bgcolor: SIGNUP_VERIFY_BUTTON_DISABLED_BG,
    color: '#f8fafc',
  },
  '&:not(.Mui-disabled)': {
    bgcolor: palettePrimaryMain,
    color: '#f8fafc',
    '&:hover': {
      bgcolor: palettePrimaryMain,
      opacity: 0.92,
    },
  },
} as const

export const signupVerifyResendLinkSx = {
  textTransform: 'none',
  color: palettePrimaryMain,
  fontSize: '14px',
  lineHeight: '20px',
  fontWeight: 400,
  minHeight: SIGNUP_VERIFY_RESEND_MIN_HEIGHT_PX,
  width: '100%',
  textAlign: 'center',
  justifyContent: 'center',
  p: 0,
  '&:hover': {
    bgcolor: 'transparent',
    textDecoration: 'underline',
  },
} as const

/** Figma Input - Box 1…6 (5074:12890–12895); flex so six cells fit the card width. */
export const signupVerifyOtpCellSx = {
  flex: '1 1 0',
  minWidth: 0,
  maxWidth: SIGNUP_VERIFY_OTP_BOX_WIDTH_PX,
  width: 'auto',
  height: { xs: 48, sm: SIGNUP_VERIFY_OTP_BOX_HEIGHT_PX },
  borderRadius: '8px',
  bgcolor: SIGNUP_VERIFY_OTP_BOX_BG,
  backgroundColor: SIGNUP_VERIFY_OTP_BOX_BG,
  border: `${SIGNUP_VERIFY_OTP_BOX_BORDER_WIDTH_PX}px solid transparent`,
  boxSizing: 'border-box',
  px: { xs: '4px', sm: `${SIGNUP_VERIFY_OTP_BOX_PADDING_X_PX}px` },
  py: { xs: '8px', sm: `${SIGNUP_VERIFY_OTP_BOX_PADDING_Y_PX}px` },
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  '&:focus-within': {
    borderColor: palettePrimaryMain,
  },
  '&[data-error="true"]': {
    borderColor: '#ef4444',
  },
  '&[data-error="true"]:focus-within': {
    borderColor: '#ef4444',
  },
  '&[data-disabled="true"]': {
    opacity: 0.6,
    pointerEvents: 'none',
  },
} as const

export const signupVerifyOtpDigitInputSx = {
  width: SIGNUP_VERIFY_OTP_DIGIT_FRAME_WIDTH_PX,
  maxWidth: '100%',
  height: { xs: '100%', sm: SIGNUP_VERIFY_OTP_DIGIT_FRAME_HEIGHT_PX },
  maxHeight: SIGNUP_VERIFY_OTP_DIGIT_FRAME_HEIGHT_PX,
  p: 0,
  m: 0,
  border: 'none',
  outline: 'none',
  background: 'transparent',
  backgroundColor: 'transparent',
  textAlign: 'center',
  fontSize: { xs: '20px', sm: `${SIGNUP_VERIFY_OTP_DIGIT_FONT_SIZE_PX}px` },
  fontWeight: 600,
  lineHeight: 'normal',
  color: SIGNUP_VERIFY_OTP_DIGIT_COLOR,
  fontFamily: 'inherit',
  boxSizing: 'border-box',
  MozAppearance: 'textfield',
  '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0,
  },
} as const
